import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter  } from 'react-router';

import DataGrid from '../components/datagrid/DataGrid';
import AlertDialog from '../components/utils/AlertDialog';
import { isLockedOrder } from '../utils/adManagerHelpers';
import { countLineItems } from '../utils/countLineItems';
import { GLO_PACKAGE, GLO_PL_FROM_PKG } from '../utils/link';
import { setEditList } from "../actions/placementActions";
import { clonePackages, deletePackages, pushPackages, saveAndNotify } from "../actions/packageActions";
import {pushPlacementsAndUpdateOrder} from "../actions/orderActions";

const IO_STATUS_CAN_PUSH = 4;


export class PackagesGrid extends Component {

  constructor(props) {
    super(props);
    this.toDelete = [];
    this.state = {
      dialog: {
        open: false,
        title: '',
        message: ''
      }
    };
    this.columns = [
      { id: 'name', label: 'Agency Package Name', link: GLO_PACKAGE, filterable: true, editable: true },
      { id: 'id', label: 'Package ID', link: GLO_PACKAGE, filterable: true, visible: true },
      { id: 'pid', label: 'Placement ID', link: GLO_PL_FROM_PKG, filterable: true, visible: false },
      { id: 'contractedQuantity', label: 'Quantity', numeric: true, totals: true, editable: true, filterable: true },
      { id: 'rate', label: 'Rate', currency: 'USD', editable: true, filterable: true },
      { id: 'rateType', label: 'Rate Type' },
      { id: 'cost', label: 'Contract Amount', numeric: true, currency: 'USD', totals: true },
      { id: 'plCount', label: 'No. of Placements', numeric: true, badge2: true, totals: true },
      { id: 'startDate', label: 'Start Date', date: 'MM/DD/YYYY', filterable: true },
      { id: 'endDate', label: 'End Date', date: 'MM/DD/YYYY', filterable: true },
    ];
    if (props.isReadOnly) {
      this.columns.forEach(c => c.editable = false);
    }
  }

  componentDidMount() {
    const { order } = this.props;
    const activeOrder = localStorage.getItem('activeOrder');
    if (parseInt(activeOrder, 10) !== order.id) {
      if (this.grid) {
        this.grid.clearFilter();
      }
      localStorage.setItem('activeOrder', order.id);
    }
  }

  onAdd = () => {
    const { order, history } = this.props;
    const { itemRemainder } = countLineItems(order);
    if (!order.Placements || itemRemainder > 0) {
      history.push(`/ios/${order.id}/package/create`);
    }
  };

  onLoad = packages => {
    this.setBatch(packages);
  };

  onSort = packages => {
    this.setBatch(packages);
  };

  onFilter = packages => {
    this.setBatch(packages);
  };

  onUpload = selected => {
    const { dispatch, order, onNotifyUpload } = this.props;
    const ids = selected.map(p => p.id);
    const pushRules = this.getPushRules();
    dispatch(pushPackages(order.id, ids, pushRules));
    onNotifyUpload (ids);
  };

  onClone = (selected, numCopies) => {
    const { dispatch, order, history } = this.props;
    const ids = selected.map(p => p.id);
    if (numCopies) {
      dispatch(clonePackages(order.id, ids, numCopies)).then(() => history.push(`/ios/${order.id}/refresh`));
    }
  };

  handlePushSettings = (pushSettings) => {
    const { dispatch, order, onNotifyUpload, history } = this.props;
    const { selected } = this.state;
    const ids = selected.map(p => p.id);
    const pushRules = this.getPushRules(pushSettings);
    dispatch(pushPlacementsAndUpdateOrder(order.id, ids, pushRules, history));
    onNotifyUpload (ids);
  };

  getPushRules = (pushSettings) => {
    const pushRules = [];
    if (pushSettings) {
      pushRules.push(pushSettings);
    }
    if (localStorage.getItem('soapLog')) {
      pushRules.push('soapLog')
    }
    if (localStorage.getItem('gamDirect')) {
      pushRules.push('gamDirect')
    }
    if (localStorage.getItem('forceStart')) {
      pushRules.push('forceStart')
    }
    return pushRules;
  };

  // We should not delete any packages with placements that have Ad Manager IDs
  onDelete = selected => {
    this.toDelete = selected.filter(pkg => !pkg.amids).map(pkg => pkg.id);
    const dialog = {};
    dialog.open = true;
    dialog.title = 'Delete packages';
    dialog.message = 'Selected package can not be deleted';
    dialog.onCancel = this.closeDialog;
    dialog.cancelTitle = 'Cancel';
    dialog.okTitle = ' ';
    if (this.toDelete.length > 0) {
      dialog.title = `${dialog.title} (${this.toDelete.length})`;
      if (this.toDelete.length > 6) {
        dialog.message = `Are you sure you want to delete ${this.toDelete.length} packages?`;
      } else {
        dialog.message = `Are you sure you want to delete packages: ${this.toDelete.join(',')}?`;
      }
      dialog.onOk = this.confirmDelete;
      dialog.okTitle = 'Delete';
      dialog.okColor = "secondary";
      dialog.cancelTitle = 'Cancel';
    }
    this.setState({ dialog });
  };

  confirmDelete = () => {
    const { dispatch, history, order } = this.props;
    this.closeDialog();
    dispatch(deletePackages(this.toDelete)).then(() => history.push(`/ios/${order.id}/refresh`));
  };

  closeDialog = () => {
    this.setState({
      dialog: {
        open: false,
        title: '',
        message: '',
        onCancel: null,
        onOk: null,
        cancelTitle: '',
        okTitle: '',
        okColor: ''
      }
    })
  };

  setBatch = packages => {
    const { dispatch } = this.props;
    const ids = packages.map(p => p.id);
    dispatch(setEditList(ids))
  };

  onCellEdit = (pack, colId, value) => {
    const { order, dispatch, history } = this.props;
    const savedVal = pack[colId];
    if (pack.name === '{PLACEMENT}') {
      alert('placement - cannot edit on packages grid');
      pack[colId] = savedVal;
      history.push(`/ios/${order.id}/refresh`);
      return;
    }
    pack[colId] = value;
    pack.cost = this.computePackageCost(pack.contractedQuantity, pack.rate, pack.rateType);
    let doUpdate = true;
    const impressions = this.sumImpressions(order, pack);
    if (impressions > pack.contractedQuantity) {
      doUpdate = window.confirm('You are changing the goal to less than has already been delivered. Doing so could pause line items in your package. Do you want to continue?');
    }
    if (doUpdate) {
      dispatch(saveAndNotify(pack));
    } else {
      pack[colId] = savedVal;
      history.push(`/ios/${order.id}/refresh`);
    }
  };

  sumImpressions = (order, pack) => {
    const pkgPlacements = order.Placements.filter(pl => pl.packageId === pack.id);
    let impressions = 0;
    pkgPlacements.forEach(pl => {
      pl.LineItems.forEach(li => impressions += li.amImpressions);
    });
    return impressions;
  };

  // Match Package.jsx: CPM /1000, Flat Fee = rate, CPD unknown (0), else qty * rate
  computePackageCost = (qty, rate, rateType) => {
    if (rateType === 'CPM') {
      const tca = qty * rate / 1000;
      return Math.round(tca * 100) / 100;
    }
    if (rateType === 'CPD') {
      return 0;
    }
    if (rateType === 'Flat Fee') {
      return rate;
    }
    return qty * rate;
  };

  /**
   * Figure out the placement count for the order which may change from clone, delete, etc.
   * @param order
   */
  recalcPlCounts = order => {
    let plCounts = new Map();
    if (order.Placements) {
      order.Placements.forEach(p => {
        if (p.packageId) {
          const thisCount = plCounts.get(p.packageId);
          if (thisCount) {
            plCounts.set(p.packageId, thisCount + 1)
          } else {
            plCounts.set(p.packageId, 1);
          }
        }
      });
    }
    if (order.Packages) {
      order.Packages.forEach(pkg => {
        if (pkg.name === '{PLACEMENT}') {
          pkg.plCount = 1;
        } else if (plCounts.get(pkg.id)) {
          pkg.plCount = plCounts.get(pkg.id);
        } else {
          pkg.plCount = 0;
        }
      });
    }
  }

  render() {
    const { order, expanded, onToggleExpand, isLocked, isReadOnly, togglePushSettings, onPushPackages, setPushData, whitelist } = this.props;
    const { dialog } = this.state;
    const { itemRemainder, itemCount } = countLineItems(order);
    const lockedOrder = isLocked || isLockedOrder(order);

    this.recalcPlCounts(order);

    return(
      <div>
        <AlertDialog {...dialog} />

        <DataGrid
          innerRef={grid => this.grid = grid}
          minWidth={window.innerWidth - 40}
          columns={this.columns}
          onCustomizeColumns={this.customizeColumns}
          sortColumn='name'
          data={order.Packages}
          title='Packages'
          sortable
          filterable
          showFilterRow
          autosaveFilters
          selectable
          pagination
          onLoad={this.onLoad}
          onFilter={this.onFilter}
          onSort={this.onSort}
          onAdd={itemRemainder > 0 && !lockedOrder ? this.onAdd : null}
          onUpload={(order && (order.IoStatusId >= IO_STATUS_CAN_PUSH || order.orderType === 'Programmatic')) ? this.onUpload : null}
          onClone={itemRemainder > 0 && !lockedOrder ? this.onClone : null}
          onDelete={!lockedOrder ? this.onDelete : null}
          onExport={this.exportAll}
          onCellEdit={this.onCellEdit}
          onSelectedExport={this.exportSelected}
          lineItemCount={itemCount}
          offsetTop={310}
          expanded={expanded}
          locked={lockedOrder}
          onToggleExpand={onToggleExpand}
          isReadOnly={isReadOnly}
          setPushData={setPushData}
          togglePushSettings={togglePushSettings}
          handlePushSettings={this.handlePushSettings}
          pushType='packages'
          onPushPackages={onPushPackages}
          whitelist={whitelist}
        />
      </div>
    )
  }
}

PackagesGrid.propTypes = {
  onNotifyUpload: PropTypes.func.isRequired,
  packages: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLocked: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  const { orderRequest } = state;
  return { packages: orderRequest.packages }
};

export default withRouter(connect(mapStateToProps)(PackagesGrid));
